import React, { useState } from 'react';
import { campoEnLinea } from '../ui/campo';
import { useIdioma } from '../../i18n/idioma';
import { createPortal } from 'react-dom';
import { EstimateItem, EstimateItemType, EstimateZone } from '../../types';
import { Trash2, Edit2, FolderOpen, Plus, Tag, CheckSquare, Square, Layers, Lock, AlertTriangle, Pin, RotateCcw } from 'lucide-react';
import { formatCurrency } from '../../utils/formatCurrency';
import { formatMarginPercent } from '../../utils/formatMargin';
import { percentagesFromRate } from '../../utils/lineItemPricing';
import { isPassThroughType } from '../../utils/passThroughPricing';
import { isUnpricedItem } from '../../utils/estimatePricingGaps';
import { isLegacyLaborRow } from '../../utils/estimateLegacyLabor';
import { StatusDot, StatusTone } from '../ui/StatusDot';
import { EmptyState } from '../ui/EmptyState';
import { Button } from '../ui/Button';

interface EstimateLineItemsProps {
    items: EstimateItem[];
    zones: EstimateZone[];
    onUpdate: (id: string, updates: Partial<EstimateItem>) => void;
    onRemove: (id: string) => void;
    onAddZone?: () => void;
    /** Removes a zone and unassigns its items. */
    onRemoveZone?: (zoneId: string) => void;
    /**
     * El factor de margen neto del estimado (la `k` de `rate = baseRate × k`,
     * PLAN-pricing-model-v2 Parte A.1). Quien lo pasa es `EstimateCalculator`,
     * que lo saca de `useEstimateForm().netMarginFactor`.
     *
     * Por defecto 1 —«exactamente mis diales»— y con 1 no se pinta nada: la
     * pastilla del factor solo aparece cuando el dial movió el estimado de su
     * ancla. Que sea opcional NO es descuido: mientras el cableado no llegue,
     * la tarjeta enseña el porcentaje propio de la fila y ni una pastilla de
     * más, que es exactamente lo correcto para k = 1.
     */
    netMarginFactor?: number;
    /**
     * ¿Se pueden tocar los precios de este estimado?
     *
     * Llega calculado por `canEditPricing` (utils/estimateEditability): solo
     * Draft y «Revisión necesaria» siguen siendo borradores; Sent / Accepted /
     * Paid son documentos que el cliente YA VIO y sus precios quedan como los
     * vio.
     *
     * El candado de VERDAD está en `hooks/useEstimateForm.ts`, que descarta los
     * campos de precio cuando el estimado está cerrado. Esta prop existe para
     * que la pantalla NO MIENTA: sin ella el campo se pintaba habilitado, el
     * contratista tecleaba un 35, y no pasaba nada — ni el número se guardaba
     * ni había forma de saber por qué. Un control que acepta lo que va a tirar
     * es peor que un control apagado.
     */
    preciosEditables?: boolean;
}

/**
 * Las partidas del estimado, una tarjeta cada una.
 *
 * ANTES había DOS interfaces para la misma fila: una tarjeta dentro de un
 * `md:hidden` y un renglón de tabla dentro de un `hidden md:block`. En
 * escritorio, meter ocho columnas —tipo, descripción, zona, cantidad, unidad,
 * costo, precio, margen, total y borrar— en el ancho que sobra al lado del
 * panel de herramientas dejaba cada campo en 100 px y los rótulos apilados
 * encima del dato. No es que se viera apretado: es que no cabía.
 *
 * Ahora es UNA sola tarjeta en todos los anchos, y la tabla se lee de arriba
 * abajo en vez de izquierda a derecha: qué es y cuánto suma en la cabecera, la
 * descripción en su propio renglón, y los números abajo en una cuadrícula que
 * se reacomoda sola (3 columnas → 2 → 1).
 *
 * Además cierra una trampa vieja: `data-unpriced-item` se pintaba DOS veces por
 * partida, así que "llévame a la siguiente sin precio" tenía que adivinar cuál
 * de las dos copias se veía (`utils/domVisible.ts`). Con una sola tarjeta ya no
 * hay copia oculta.
 */
const EstimateLineItems: React.FC<EstimateLineItemsProps> = ({ items, zones = [], onUpdate, onRemove, onAddZone, onRemoveZone, netMarginFactor = 1, preciosEditables = true }) => {
    const { t } = useIdioma();
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    // Replace the native `confirm()` for bulk delete with a styled modal —
    // the browser dialog showed an unbranded "razaproapp.com dice" header.
    const [confirmingBulkDelete, setConfirmingBulkDelete] = useState(false);

    // Display helper for numeric inputs — coerce undefined / NaN / 0 to '' so
    // React doesn't trip the "value prop on input should not be null" warning
    // and the field shows the placeholder instead of a literal "0".
    const numInput = (n: number | null | undefined): number | '' => {
        if (n === null || n === undefined || !Number.isFinite(n) || n === 0) return '';
        return Math.round(n * 100) / 100;
    };

    /**
     * Gemelo de `numInput` para el porcentaje, con UNA diferencia que importa:
     * el CERO se enseña.
     *
     * En un costo, 0 es «todavía no lo sé» y el hueco invita a teclearlo. En un
     * porcentaje, 0 es una decisión —«esto se cobra a costo»— y vaciarle el
     * campo al contratista que acaba de escribir 0 parece que no se guardó.
     */
    const pctInput = (n: number | null | undefined): number | '' => {
        if (n === null || n === undefined || !Number.isFinite(n)) return '';
        return Math.round(n * 100) / 100;
    };

    /* La pastilla del factor global (Parte A.5).
     *
     * Se pinta APARTE del porcentaje de la fila y nunca multiplicada con él. El
     * porcentaje compuesto —el 20% de markup por el ×1,113 que da «33,6%»— no
     * cuadra con ningún dial de la pantalla: no es el que el contratista tecleó
     * en la fila ni el que pidió en el dial del estimado. Ese número inventado
     * es el «43%» que arrancó esta auditoría entera.
     *
     * El umbral es medio milésimo porque la pastilla enseña tres decimales: un
     * k de 1,0001 se pintaría «×1.000», una pastilla que no dice nada. */
    const kFactor = Number.isFinite(netMarginFactor) && netMarginFactor > 0 ? netMarginFactor : 1;
    const muestraFactor = Math.abs(kFactor - 1) >= 0.0005;

    // Unidades que se ofrecen para cambiar la de una partida. La unidad real de
    // la fila se agrega SIEMPRE a la lista: el corpus de plantillas trae cosas
    // como 'cu yd', 'slab' o 'bag', y un <select> cuyo `value` no está entre sus
    // opciones se pinta VACÍO — parecería que la partida perdió su unidad, y el
    // primer clic se la cambiaría de verdad.
    const UNIDADES = ['EA', 'HR', 'DAY', 'SF', 'SY', 'LF', 'CY', 'TON', 'GAL', 'LS'];

    /* `campoEnLinea` y NO `campo`: aquí cada fila lleva cuatro campos, y con el
       marco de un campo de formulario una lista de veinte partidas se ve como
       una reja. Esta variante existe en el sistema por esta pantalla — no se
       aplastó contra la otra al unificar. */
    const inputClass = campoEnLinea({ tamaño: 'text-[14px]' });

    // La descripción es el título de la tarjeta, no una celda: pesa más y ocupa
    // su propio renglón de punta a punta.
    const descInputClass = campoEnLinea({ alto: 'auto', tamaño: 'text-[15px]', extra: 'font-semibold leading-snug' });

    // Los controles que se abren (unidad, zona) sí llevan caja: a diferencia de
    // un número, no se descubren solos — si no se ven, nadie sabe que se pueden
    // tocar.
    const selectClass = "h-10 rounded-xl border border-acero-200 bg-white px-3 text-[13px] text-navy-950 cursor-pointer transition-[border-color,box-shadow] duration-150 hover:border-acero-300 focus:outline-none focus:border-navy-600 focus:ring-2 focus:ring-brand-400/55";

    const fieldLabelClass = "label-ui block mb-1.5";

    // Labor lines: cost/rate come from the overhead calculator in Settings and
    // must not be editable on the estimate. Only quantity changes per estimate.
    const lockedDisplayClass = "w-full h-10 bg-acero-50 border border-transparent rounded-xl px-3 text-[14px] text-acero-500 cursor-not-allowed select-none inline-flex items-center justify-end gap-1.5 font-data tabular-nums";
    // Gemelo del anterior para la unidad. Se escribe entero en vez de tapar el
    // `cursor-pointer` de `selectClass` con otra clase: entre dos utilidades de
    // la misma propiedad gana la que Tailwind emita después en la hoja, no la
    // que se escriba después en el atributo.
    const lockedUnitClass = "h-10 shrink-0 rounded-xl border border-transparent bg-acero-50 px-3 text-[13px] text-acero-500 cursor-not-allowed select-none inline-flex items-center gap-1.5";
    const isLaborLocked = (item: EstimateItem) => item.type === 'Labor';
    const laborLockTitle = t('estimado.partidas.manoObraBloqueada2');
    const unitLockTitle = t('estimado.partidas.unidadBloqueada');

    // Material/equipment/fee lines at $0 cost — surface a subtle visual hint
    // so contractors know to set a price (which then persists to the workspace
    // pricebook on save). Labor at $0 wouldn't make sense; skip it there.
    const isZeroCostMaterial = (item: EstimateItem) => isUnpricedItem(item);
    const zeroCostHint = t('estimado.partidas.sinCosto');

    // Neutral type selector — every line type reads the same; the row's meaning
    // comes from its description and numbers, not from a pastel chip. El tinte
    // es el «info» del sistema (navy-50), no un azul de catálogo.
    const typeSelectClass = "h-8 appearance-none px-2.5 rounded-lg text-[12px] font-semibold bg-navy-50 border border-navy-100 text-navy-700 cursor-pointer transition-[border-color,box-shadow] duration-150 hover:border-navy-200 focus:outline-none focus:border-navy-600 focus:ring-2 focus:ring-brand-400/55";

    /* El semáforo se decide SIEMPRE sobre el margen (ganancia sobre el precio),
     * nunca sobre el markup (ganancia sobre el costo), aunque la fila enseñe el
     * markup. Son escalas distintas: un material con 20% de markup deja 16,67%
     * de margen, así que juzgarlo con estos umbrales lo pintaba «normal»
     * (navy) cuando en realidad merece ámbar. Con el ajuste por defecto del
     * taller, TODOS los materiales salían mal calificados. */
    const marginTone = (margin?: number): StatusTone => {
        if (!margin || margin <= 0) return 'peligro';
        if (margin < 20) return 'aviso';
        if (margin < 35) return 'info';
        return 'exito';
    };

    // Group items by Zone
    const groupedItems = React.useMemo(() => {
        const groups: Record<string, EstimateItem[]> = { 'unassigned': [] };
        const knownZoneIds = new Set(zones.map(z => z.id));
        zones.forEach(z => groups[z.id] = []);
        items.forEach(item => {
            const zId = item.zoneId || 'unassigned';
            // If the item's zone doesn't exist (deleted or orphan 'default'), render as unassigned
            if (zId === 'unassigned' || !knownZoneIds.has(zId)) {
                groups['unassigned'].push(item);
            } else {
                groups[zId].push(item);
            }
        });
        return groups;
    }, [items, zones]);

    const toggleSelect = (id: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selectedIds.size === items.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(items.map(i => i.id)));
        }
    };

    const handleBulkDelete = () => {
        if (selectedIds.size === 0) return;
        setConfirmingBulkDelete(true);
    };

    const confirmBulkDelete = () => {
        selectedIds.forEach(id => onRemove(id));
        setSelectedIds(new Set());
        setConfirmingBulkDelete(false);
    };

    const handleBulkMoveZone = (zoneId: string) => {
        selectedIds.forEach(id => onUpdate(id, { zoneId: zoneId === 'unassigned' ? undefined : zoneId }));
        setSelectedIds(new Set());
    };

    const handleBulkChangeType = (type: EstimateItemType) => {
        selectedIds.forEach(id => onUpdate(id, { type }));
        setSelectedIds(new Set());
    };

    /* El vacío global solo cuando NO HAY NADA: ni partidas NI zonas.
     *
     * Antes bastaba con `items.length === 0`, y ese `return` temprano se comía
     * el render de las zonas. El efecto era el que se reportó: en un estimado
     * nuevo, crear una zona no hacía nada visible. La zona SÍ se creaba —el
     * resumen de arriba pasaba a decir «1 Zones»— pero la lista seguía
     * enseñando «El estimado está vacío», así que parecía que el botón no
     * funcionaba. Agregar una partida cualquiera la hacía aparecer de golpe,
     * junto con la zona que llevaba ahí desde el principio.
     *
     * Lo de abajo ya sabía pintar una zona sin partidas: `renderGroup` tiene su
     * propia tarjeta punteada de «zona vacía» (y salta el grupo «sin zona»
     * cuando no tiene nada). Lo único que faltaba era dejarlo llegar. */
    if (items.length === 0 && zones.length === 0) {
        return (
            <div className="bg-white rounded-2xl border border-acero-200">
                <EmptyState
                    icon={<Edit2 size={18} />}
                    title={t('estimado.partidas.vacio.titulo')}
                    description={t('estimado.partidas.vacio.texto')}
                />
            </div>
        );
    }

    // Bulk Action Bar
    const BulkBar = () => {
        if (selectedIds.size === 0) return null;
        const bulkSelectClass = "h-8 bg-navy-800 text-white text-xs font-medium rounded-lg px-2.5 cursor-pointer border border-navy-700 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400";
        return (
            <div className="sticky top-0 z-10 flex items-center justify-between bg-navy-850 text-white px-5 py-3 rounded-xl mb-5 shadow-s2 animate-in slide-in-from-top-4">
                <span className="text-sm font-medium"><span className="font-data tabular-nums">{selectedIds.size}</span> {t('estimado.partidas.seleccionadas')}</span>
                <div className="flex items-center gap-3">
                    {zones.length > 0 && (
                        <select
                            className={bulkSelectClass}
                            defaultValue=""
                            onChange={(e) => { if (e.target.value) handleBulkMoveZone(e.target.value) }}
                        >
                            <option value="" disabled>{t('estimado.partidas.moverZona')}</option>
                            <option value="unassigned">{t('estimado.partidas.sinZona.opcion')}</option>
                            {zones.map(z => <option key={z.id} value={z.id}>{z.name}</option>)}
                        </select>
                    )}
                    <select
                        className={bulkSelectClass}
                        defaultValue=""
                        onChange={(e) => { if (e.target.value) handleBulkChangeType(e.target.value as EstimateItemType) }}
                    >
                        <option value="" disabled>{t('estimado.partidas.cambiarTipo')}</option>
                        <option value="Labor">{t('estimado.partidas.tipo.manoDeObra')}</option>
                        <option value="Material">{t('estimado.partidas.tipo.material')}</option>
                        <option value="Equipment">{t('estimado.partidas.tipo.maquinaria')}</option>
                        <option value="Subcontractor">{t('estimado.partidas.tipo.subcontratista')}</option>
                        <option value="Fee">{t('estimado.partidas.tipo.cargo')}</option>
                    </select>
                    <button
                        onClick={handleBulkDelete}
                        className="p-2 text-white/80 hover:text-white hover:bg-navy-800 rounded-lg transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                        title={t('estimado.partidas.borrarSel.title')}
                        aria-label={t('estimado.partidas.borrarSel.aria')}
                    >
                        <Trash2 size={14} />
                    </button>
                    <button
                        onClick={() => setSelectedIds(new Set())}
                        className="text-xs font-medium text-white/70 hover:text-white hover:underline underline-offset-4 ml-1 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 rounded"
                    >
                        {t('estimado.partidas.quitarSeleccion')}
                    </button>
                </div>
            </div>
        );
    };

    /** Una partida. Cabecera (qué es / cuánto deja / cuánto suma) → descripción → números. */
    const renderCard = (item: EstimateItem) => {
        const isSelected = selectedIds.has(item.id);
        const locked = isLaborLocked(item);
        const isMaterial = item.type === 'Material';
        /* Los materiales se cobran por MARKUP (sobre el costo) y todo lo demás
         * por MARGEN (sobre el precio). La app siempre enseñó las dos cosas en
         * el mismo sitio y sin decir cuál era cuál: una fila de mano de obra a
         * $142,86 sobre $100 decía "30%" y una de material a $130,00 sobre
         * $100 también decía "30%" —la primera deja $42,86 y la segunda
         * $30,00—. Ahora la etiqueta viaja pegada al número. */
        const pct = (isMaterial ? item.markupPercent : item.marginPercent) || 0;
        const pctLabel = isMaterial ? t('estimado.partidas.markup') : t('estimado.partidas.margen');

        /* Un pase directo NO lleva campo de porcentaje: AUSENTE, no vacío ni
         * deshabilitado (Parte A.3). Un permiso de $400 se remite en $400 y el
         * cliente puede leer la cifra en el papel del municipio; un campo
         * apagado invita a preguntarse qué habría que hacer para encenderlo. */
        const esPaseDirecto = isPassThroughType(item.type);
        // La base sobre la que se mide el porcentaje, dicha en voz alta. Es lo
        // único que separa un 30% que deja $30 de uno que deja $42,86, y hasta
        // hoy solo vivía en el color de una insignia `aria-hidden`.
        const baseDelPct = isMaterial ? t('estimado.partidas.sobreCosto') : t('estimado.partidas.sobrePrecio');
        const pctFieldId = `${item.id}-pct`;
        const estaFijada = item.pricePinned === true;

        /* El eco en dinero (Parte A.5), la respuesta a «solo quiero 30%».
         *
         * Se lee del precio BASE, no del que se está cobrando: el porcentaje de
         * la fila describe su línea base, y el factor global viaja aparte en su
         * pastilla. Mezclarlos aquí volvería a fabricar el compuesto.
         *
         * `percentagesFromRate` es quien hace las dos divisiones; aquí no se
         * calcula ni un margen (invariante A.6.1). Sin costo o sin precio no
         * hay eco: «$0.00 → $0.00 · te llevas $0.00» no le enseña nada a nadie. */
        const costoFila = Number.isFinite(item.cost) ? item.cost : 0;
        const baseFila = Number.isFinite(item.baseRate as number) ? (item.baseRate as number) : item.rate;
        const hayEco = !esPaseDirecto && costoFila > 0 && baseFila > 0;
        const otraLectura = percentagesFromRate(costoFila, baseFila);
        const eco = hayEco
            ? `${formatCurrency(costoFila)} → ${formatCurrency(baseFila)} · ${t('estimado.partidas.teLlevas')} ${formatCurrency(baseFila - costoFila)} · ${formatMarginPercent(isMaterial ? otraLectura.marginPercent : otraLectura.markupPercent)} ${isMaterial ? t('estimado.partidas.deMargen') : t('estimado.partidas.deMarkup')}`
            : null;

        const unitCode = item.unit || 'EA';
        const opcionesUnidad = UNIDADES.includes(unitCode) ? UNIDADES : [unitCode, ...UNIDADES];
        const qtyLabelId = `${item.id}-cant`;

        return (
            <article
                key={item.id}
                data-unpriced-item={isZeroCostMaterial(item) ? item.id : undefined}
                className={`bg-white rounded-2xl border transition-[border-color,box-shadow] duration-150 ${isSelected ? 'border-navy-300 shadow-s1' : 'border-acero-200 hover:border-acero-300'}`}
            >
                {/* ── Cabecera ── */}
                <div className="px-5 pt-4">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                        <button
                            onClick={() => toggleSelect(item.id)}
                            aria-label={isSelected ? t('estimado.partidas.quitarSeleccion') : t('estimado.partidas.seleccionar')}
                            className={`shrink-0 transition-colors duration-150 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${isSelected ? 'text-navy-950' : 'text-acero-400 hover:text-navy-950'}`}
                        >
                            {isSelected ? <CheckSquare size={16} /> : <Square size={16} />}
                        </button>
                        <select
                            className={typeSelectClass}
                            value={item.type}
                            aria-label={t('estimado.partidas.col.tipo')}
                            onChange={(e) => onUpdate(item.id, { type: e.target.value as EstimateItemType })}
                        >
                            <option value="Labor">{t('estimado.partidas.tipo.manoDeObra')}</option>
                            <option value="Material">Material</option>
                            <option value="Equipment">{t('estimado.partidas.tipo.maquinaria')}</option>
                            <option value="Subcontractor">Sub</option>
                            <option value="Fee">{t('estimado.partidas.tipo.cargo')}</option>
                        </select>
                        {item.category && (
                            <span className="label-ui inline-flex items-center gap-1 min-w-0">
                                <Tag size={10} className="shrink-0" />
                                <span className="truncate max-w-[160px]">{item.category}</span>
                            </span>
                        )}
                        {/* Margen y total: lo primero que se busca al repasar el estimado. */}
                        <div className="ml-auto flex items-center gap-4 shrink-0">
                            {/* El tono se juzga sobre el margen; el número que se
                                enseña es el de la métrica que rige a este tipo de
                                fila, con su nombre al lado. El `title` no bastaba:
                                en teléfono no hay hover y StatusDot es aria-hidden,
                                así que la aclaración no existía ni para el dedo ni
                                para el lector de pantalla. */}
                            {!esPaseDirecto && (
                                <StatusDot tone={marginTone(item.marginPercent)}>
                                    <span
                                        className="font-data tabular-nums"
                                        aria-label={`${formatMarginPercent(pct)} ${pctLabel} ${baseDelPct}`}
                                    >
                                        {formatMarginPercent(pct)}
                                        <span className="ml-1 text-[10px] font-medium uppercase tracking-wide opacity-70">
                                            {pctLabel.replace(' %', '').replace('%', '').trim()}
                                        </span>
                                    </span>
                                </StatusDot>
                            )}
                            {/* La pastilla del factor: al LADO del porcentaje propio,
                                nunca dentro de él. Lo que se lee es «20,0% markup ×1.113»,
                                dos números que sí cuadran con dos diales. */}
                            {muestraFactor && !esPaseDirecto && (
                                <span
                                    className="font-data tabular-nums text-[11px] font-semibold text-navy-700 bg-navy-50 border border-navy-100 rounded-lg px-1.5 py-0.5 whitespace-nowrap"
                                    title={t('estimado.partidas.factorNeto')}
                                    aria-label={`${t('estimado.partidas.factorNeto')}: ×${kFactor.toFixed(3)}`}
                                >
                                    ×{kFactor.toFixed(3)}
                                </span>
                            )}
                            <span className="font-data tabular-nums font-semibold text-navy-950 text-[17px] whitespace-nowrap">
                                {formatCurrency(item.total)}
                            </span>
                        </div>
                    </div>

                    <div className="mt-3 pb-4">
                        <input
                            className={descInputClass}
                            value={item.description}
                            aria-label={t('estimado.partidas.col.descripcion')}
                            onChange={(e) => onUpdate(item.id, { description: e.target.value })}
                            placeholder={t('estimado.partidas.descripcion.placeholder')}
                        />
                    </div>
                </div>

                {/* ── Números ── */}
                <div className="border-t border-acero-100 px-5 py-4 space-y-4">
                    <div className={`grid grid-cols-1 sm:grid-cols-2 ${esPaseDirecto ? 'lg:grid-cols-3' : 'lg:grid-cols-4'} gap-x-5 gap-y-4 items-start`}>
                        {/* Cantidad + unidad */}
                        <div>
                            <div className="flex items-center gap-2 mb-1.5 min-w-0">
                                <span className="label-ui shrink-0" id={qtyLabelId}>
                                    {locked ? t('estimado.partidas.unidad.horas') : t('estimado.partidas.campo.cantidad')}
                                </span>
                                {/* De dónde salió la cantidad (una calculadora, una plantilla).
                                    Va pegado al rótulo porque explica ESTE número, no la fila. */}
                                {item.calcBasis && (
                                    <span
                                        className="label-ui !text-navy-700 bg-navy-50 rounded px-1.5 py-0.5 truncate"
                                        title={item.calcBasis}
                                    >
                                        {item.calcBasis}
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="number"
                                    aria-labelledby={qtyLabelId}
                                    className={`${inputClass} font-data tabular-nums`}
                                    value={numInput(item.quantity)}
                                    placeholder="0"
                                    onFocus={(e) => e.target.select()}
                                    onChange={(e) => onUpdate(item.id, { quantity: parseFloat(e.target.value) || 0 })}
                                />
                                {/* La unidad de la mano de obra NO se toca: es el discriminador
                                    que usa `isLegacyLaborRow` para saber si una fila vieja está
                                    cobrada en medida de cliente en vez de horas-hombre. Dejarla
                                    editable haría que cambiar "HR" por "SF" encendiera solo el
                                    aviso de fila mal cobrada. */}
                                {locked ? (
                                    <span className={lockedUnitClass} title={unitLockTitle}>
                                        {unitCode}
                                        <Lock size={10} className="text-acero-400 shrink-0" />
                                    </span>
                                ) : (
                                    <select
                                        className={`${selectClass} shrink-0`}
                                        aria-label={t('estimado.partidas.campo.unidad')}
                                        value={unitCode}
                                        onChange={(e) => onUpdate(item.id, { unit: e.target.value })}
                                    >
                                        {opcionesUnidad.map(u => <option key={u} value={u}>{u}</option>)}
                                    </select>
                                )}
                            </div>
                            {/* Fila anterior al Esquema v4: su cantidad es la medida del
                                cliente, no horas-hombre, así que se está costeando bajo.
                                No se corrige sola — un estimado enviado es un documento. */}
                            {isLegacyLaborRow(item) && (
                                <span className="mt-1.5 block text-[11px] leading-tight text-aviso" title={t('estimado.partida.horasViejas')}>
                                    {t('estimado.partida.horasViejas.corto')}
                                </span>
                            )}
                        </div>

                        {/* Costo */}
                        <label className="block">
                            <span className={fieldLabelClass}>{t('estimado.partidas.campo.costo')}</span>
                            {locked ? (
                                <div className={lockedDisplayClass} title={laborLockTitle}>
                                    <span className="mr-auto text-acero-400">$</span>
                                    {(item.cost ?? 0).toFixed(2)}
                                    <Lock size={10} className="text-acero-400 shrink-0" />
                                </div>
                            ) : (
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-acero-400 text-[13px] pointer-events-none">$</span>
                                    <input
                                        type="number"
                                        step="0.01"
                                        className={`${inputClass} text-right !pl-7 font-data tabular-nums ${isZeroCostMaterial(item) ? '!border-brand-400 bg-brand-50/50' : ''}`}
                                        title={isZeroCostMaterial(item) ? zeroCostHint : undefined}
                                        value={numInput(item.cost)}
                                        placeholder="0.00"
                                        onFocus={(e) => e.target.select()}
                                        onChange={(e) => onUpdate(item.id, { cost: parseFloat(e.target.value) || 0 })}
                                    />
                                </div>
                            )}
                        </label>

                        {/* Precio */}
                        <label className="block">
                            <span className={fieldLabelClass}>{t('estimado.partidas.campo.precio')}</span>
                            {locked ? (
                                <div className={lockedDisplayClass} title={laborLockTitle}>
                                    <span className="mr-auto text-acero-400">$</span>
                                    {(item.rate ?? 0).toFixed(2)}
                                    <Lock size={10} className="text-acero-400 shrink-0" />
                                </div>
                            ) : (
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-acero-400 text-[13px] pointer-events-none">$</span>
                                    <input
                                        type="number"
                                        step="0.01"
                                        className={`${inputClass} text-right !pl-7 font-data tabular-nums`}
                                        value={numInput(item.rate)}
                                        placeholder="0.00"
                                        onFocus={(e) => e.target.select()}
                                        onChange={(e) => onUpdate(item.id, { rate: parseFloat(e.target.value) || 0 })}
                                    />
                                </div>
                            )}
                        </label>

                        {/* Porcentaje — la Capa 2 del modelo (Parte A.3).
                            «A veces el material cuesta $1 y solo quieren ponerle
                            30%»: hasta hoy no había dónde escribir ese 30. El
                            número vivía en un <span> de solo lectura dentro de un
                            StatusDot, y la única forma de mover el precio de una
                            fila era teclear la tarifa entera a mano.

                            EL METRO LO DECIDE EL TIPO, no este campo: Material va
                            por MARKUP (sobre el costo) y el resto por MARGEN
                            (sobre el precio). Por eso se manda `markupPercent` o
                            `marginPercent` según el tipo — el hook fija el pin con
                            el metro que corresponde y recalcula el `baseRate`.

                            LA MANO DE OBRA LO TIENE EDITABLE aunque su costo y su
                            tarifa estén bloqueados por la calculadora de gastos
                            fijos: es el ÚNICO control que le queda a esa fila. Sin
                            él, un contratista que quiera bajarle el margen a una
                            partida de mano de obra no tiene ninguna salida en
                            pantalla. */}
                        {!esPaseDirecto && (
                            <div>
                                <label className={fieldLabelClass} htmlFor={pctFieldId}>{pctLabel}</label>
                                <div className="relative">
                                    <input
                                        id={pctFieldId}
                                        type="number"
                                        step="0.1"
                                        className={`${inputClass} text-right !pr-7 font-data tabular-nums ${estaFijada ? '!border-navy-300 bg-navy-50/60' : ''}`}
                                        value={pctInput(pct)}
                                        placeholder="0"
                                        aria-label={`${pctLabel}, ${baseDelPct}`}
                                        disabled={!preciosEditables}
                                        onFocus={(e) => e.target.select()}
                                        onChange={(e) => {
                                            const n = parseFloat(e.target.value) || 0;
                                            onUpdate(item.id, isMaterial ? { markupPercent: n } : { marginPercent: n });
                                        }}
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-acero-400 text-[13px] pointer-events-none">%</span>
                                </div>
                                {/* El eco en dinero, pegado debajo del campo. Es la
                                    diferencia entre markup y margen dicha en dólares
                                    en vez de en teoría: sobre 2.000 unidades, 30 de
                                    markup y 30 de margen se separan $257. */}
                                {eco && (
                                    <p className="mt-1.5 text-[11px] leading-snug text-acero-500 font-data tabular-nums">
                                        {eco}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>

                    {/* La marca de fijado a mano y la puerta de vuelta.
                        Fijar TIENE que ser reversible o nadie se atreve a usarlo:
                        sin este botón, el contratista que teclea un 30 de prueba en
                        una fila no sabe cómo devolverla a la tarifa del taller, y
                        la conclusión que saca es «mejor no toco esto».
                        El pin es una LÍNEA BASE, no un candado (Parte A.3): el dial
                        de margen neto sigue moviendo la fila con todas las demás. */}
                    {estaFijada && !esPaseDirecto && (
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-navy-700 bg-navy-50 border border-navy-100 rounded-lg px-2 py-1">
                                <Pin size={11} className="shrink-0" />
                                {t('estimado.partidas.fijadoAMano')}
                            </span>
                            <button
                                type="button"
                                onClick={() => onUpdate(item.id, { pricePinned: false, pinnedPercent: undefined, pinnedMetric: undefined })}
                                disabled={!preciosEditables}
                                className="inline-flex items-center gap-1.5 text-[11px] font-medium text-acero-500 hover:text-navy-950 hover:underline underline-offset-4 rounded transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                            >
                                <RotateCcw size={11} className="shrink-0" />
                                {t('estimado.partidas.volverAlTaller')}
                            </button>
                        </div>
                    )}

                    {/* Zona + borrar. El borrar vive abajo a la derecha y SIEMPRE se ve:
                        el patrón de "aparece al pasar el cursor" es invisible en el
                        teléfono, donde no hay cursor. */}
                    <div className="flex items-end justify-between gap-4">
                        {zones.length > 0 ? (
                            <label className="block min-w-0 w-full max-w-[280px]">
                                <span className={fieldLabelClass}>{t('estimado.partidas.campo.zona')}</span>
                                <select
                                    className={`${selectClass} w-full`}
                                    value={item.zoneId || 'unassigned'}
                                    onChange={(e) => onUpdate(item.id, { zoneId: e.target.value === 'unassigned' ? undefined : e.target.value })}
                                >
                                    <option value="unassigned">{t('estimado.partidas.sinZona.opcion')}</option>
                                    {zones.map(z => <option key={z.id} value={z.id}>{z.name}</option>)}
                                </select>
                            </label>
                        ) : <span />}
                        <button
                            onClick={() => onRemove(item.id)}
                            aria-label={t('estimado.partidas.quitar')}
                            title={t('estimado.partidas.quitar')}
                            className="shrink-0 p-2.5 text-acero-400 hover:text-peligro hover:bg-peligro-bg rounded-xl transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                        >
                            <Trash2 size={16} />
                        </button>
                    </div>
                </div>
            </article>
        );
    };

    const renderGroup = (getItems: EstimateItem[], zoneId: string, zoneName: string) => {
        if (getItems.length === 0 && zoneId === 'unassigned') return null;

        const zoneTotal = getItems.reduce((s, i) => s + i.total, 0);
        /* `0 === 0` es verdadero: en una zona recién creada, sin partidas, la
         * casilla de «seleccionar todo» se pintaba MARCADA. Exigir que haya algo
         * que seleccionar. */
        const todoSeleccionado = items.length > 0 && selectedIds.size === items.length;
        const isUnassignedGroup = zoneId === 'unassigned';

        return (
            <div key={zoneId} className="mb-10 last:mb-0">
                {/* Encabezado del grupo. El de «sin zona» se pinta aunque no sea
                    una zona de verdad: sin él, borrar la última zona no dejaba
                    ninguna señal en pantalla de que algo había cambiado. */}
                <div className="flex items-center justify-between gap-3 mb-4 px-1">
                    <div className="flex items-center gap-3 min-w-0">
                        <button
                            onClick={toggleSelectAll}
                            aria-label={t('estimado.partidas.seleccionarTodo')}
                            className={`shrink-0 transition-colors duration-150 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${todoSeleccionado ? 'text-navy-950' : 'text-acero-400 hover:text-navy-950'}`}
                        >
                            {todoSeleccionado ? <CheckSquare size={15} /> : <Square size={15} />}
                        </button>
                        {isUnassignedGroup
                            ? <Layers size={15} className="text-acero-400 shrink-0" />
                            : <FolderOpen size={15} className="text-acero-400 shrink-0" />}
                        <div className="flex items-baseline gap-2 min-w-0">
                            <h4 className="font-semibold text-navy-950 text-sm tracking-tight truncate">
                                {isUnassignedGroup ? t('estimado.partidas.sinZona.opcion') : zoneName}
                            </h4>
                            <span className="label-ui whitespace-nowrap">
                                <span className="font-data tabular-nums">{getItems.length}</span>{' '}
                                {isUnassignedGroup
                                    ? t('estimado.partidas.conteo', { count: getItems.length })
                                    : t('estimado.partidas.conteoZona')}
                            </span>
                        </div>
                    </div>
                    {!isUnassignedGroup && onRemoveZone && (
                        <button
                            onClick={() => onRemoveZone(zoneId)}
                            className="shrink-0 p-1.5 text-acero-400 hover:text-peligro hover:bg-peligro-bg rounded-lg transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                            title={t('estimado.partidas.borrarZona.title')}
                            aria-label={`${t('estimado.partidas.borrarConfirm.titulo')} zone ${zoneName}`}
                        >
                            <Trash2 size={14} />
                        </button>
                    )}
                </div>

                <div className="space-y-4">
                    {getItems.map(item => renderCard(item))}
                </div>

                {getItems.length === 0 && !isUnassignedGroup && (
                    <div className="bg-white rounded-2xl border border-dashed border-acero-300">
                        <EmptyState
                            variant="filtro"
                            title={t('estimado.partidas.zonaVacia.titulo')}
                            description={t('estimado.partidas.zonaVacia.texto')}
                        />
                    </div>
                )}

                {getItems.length > 0 && (
                    <div className="mt-4 flex items-center justify-between gap-4 rounded-2xl border border-acero-200 bg-acero-50 px-5 py-3">
                        <span className="label-ui">
                            {isUnassignedGroup ? t('estimado.partidas.totalSinZona') : t('estimado.partidas.totalZona', { zona: zoneName })}
                        </span>
                        <span className="font-data tabular-nums font-semibold text-navy-950 text-[15px] whitespace-nowrap">
                            {formatCurrency(zoneTotal)}
                        </span>
                    </div>
                )}
            </div>
        );
    };

    return (
        // `notranslate` / translate="no": this is the estimate builder's
        // highest-churn subtree — zones and rows insert/remove/reorder
        // constantly. Browser auto-translate rewrites text nodes here and
        // React's reconciler then fails with NotFoundError on insertBefore.
        // Scoped to this list only, so the rest of the app stays translatable.
        <div className="space-y-6 pb-8 notranslate" translate="no">
            <BulkBar />
            {zones.map(z => renderGroup(groupedItems[z.id] || [], z.id, z.name))}
            {renderGroup(groupedItems['unassigned'] || [], 'unassigned', t('estimado.partidas.grupoSinZona'))}

            {onAddZone && (
                <Button variant="outline" onClick={onAddZone} className="w-full" leftIcon={<Plus size={16} />}>
                    {t('estimado.partidas.agregarZona')}
                </Button>
            )}

            {confirmingBulkDelete && createPortal(
                <div className="fixed inset-0 z-[300] bg-navy-950/50 flex items-center justify-center p-4">
                    <div className="bg-white w-full max-w-md rounded-3xl shadow-s3 overflow-hidden">
                        <div className="p-5 flex items-start gap-4 border-b border-acero-100">
                            <div className="w-10 h-10 rounded-xl bg-peligro-bg text-peligro grid place-items-center shrink-0">
                                <AlertTriangle size={18} />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-[17px] font-semibold tracking-tight text-navy-950">
                                    {t('comun.borrar')} <span className="font-data tabular-nums">{selectedIds.size}</span> {t('estimado.partidas.conteo', { count: selectedIds.size })}?
                                </h3>
                                <p className="text-sm text-acero-500 mt-1.5">
                                    {t('estimado.partidas.borrarSeleccion', { count: selectedIds.size })}
                                </p>
                            </div>
                        </div>
                        <div className="p-4 bg-acero-50 flex gap-3 justify-end">
                            <Button variant="ghost" onClick={() => setConfirmingBulkDelete(false)}>
                                {t('comun.cancelar')}
                            </Button>
                            <Button variant="danger" onClick={confirmBulkDelete} leftIcon={<Trash2 size={14} />}>
                                {t('comun.borrar')}
                            </Button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default EstimateLineItems;
